import { useMemo } from 'react';
import {
  makeStyles,
  tokens,
  Body1,
  Button,
  Card,
  CardHeader,
  Spinner,
  Text,
} from '@fluentui/react-components';
import { CalendarRegular } from '@fluentui/react-icons';
import type { TimeSlot } from '../types/booking.types';

interface SlotPickerProps {
  slots: TimeSlot[];
  timezone: string;
  selectedSlot: TimeSlot | null;
  onSelectSlot: (slot: TimeSlot) => void;
  isLoading: boolean;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalL,
  },
  daysGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: tokens.spacingHorizontalM,
  },
  dayCard: {
    padding: tokens.spacingHorizontalM,
  },
  dayHeader: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    marginBottom: tokens.spacingVerticalS,
  },
  dayDate: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  slotsColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalSNudge,
    marginTop: tokens.spacingVerticalS,
  },
  slotButton: {
    justifyContent: 'flex-start',
    width: '100%',
  },
  staffLabel: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    marginLeft: tokens.spacingHorizontalS,
  },
  emptyState: {
    padding: tokens.spacingHorizontalXXXL,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    textAlign: 'center',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: '40px 0',
  },
});

/** Group slots by day (in the configured timezone). */
function groupByDay(
  slots: TimeSlot[],
  timezone: string
): Map<string, { dayLabel: string; dateLabel: string; slots: TimeSlot[] }> {
  const groups = new Map<
    string,
    { dayLabel: string; dateLabel: string; slots: TimeSlot[] }
  >();

  const dayFormatter = new Intl.DateTimeFormat('en-AU', {
    weekday: 'long',
    timeZone: timezone,
  });
  const dateFormatter = new Intl.DateTimeFormat('en-AU', {
    day: 'numeric',
    month: 'short',
    timeZone: timezone,
  });
  const keyFormatter = new Intl.DateTimeFormat('en-AU', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: timezone,
  });

  for (const slot of slots) {
    const d = new Date(slot.start);
    const key = keyFormatter.format(d);
    if (!groups.has(key)) {
      groups.set(key, {
        dayLabel: dayFormatter.format(d),
        dateLabel: dateFormatter.format(d),
        slots: [],
      });
    }
    groups.get(key)!.slots.push(slot);
  }

  return groups;
}

export function SlotPicker({
  slots,
  timezone,
  selectedSlot,
  onSelectSlot,
  isLoading,
}: SlotPickerProps) {
  const styles = useStyles();

  const timeFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat('en-AU', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: timezone || 'Australia/Sydney',
      }),
    [timezone]
  );

  const days = useMemo(
    () => groupByDay(slots, timezone || 'Australia/Sydney'),
    [slots, timezone]
  );

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Spinner size="large" label="Loading available times..." />
      </div>
    );
  }

  if (slots.length === 0) {
    return (
      <Card className={styles.emptyState}>
        <CalendarRegular fontSize={40} color={tokens.colorNeutralForeground3} />
        <Body1>
          No available slots found for the next few days. Please try again
          later or contact us directly.
        </Body1>
      </Card>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.daysGrid}>
        {Array.from(days.entries()).map(([key, { dayLabel, dateLabel, slots: daySlots }]) => (
          <Card key={key} className={styles.dayCard}>
            <CardHeader
              header={<Text className={styles.dayHeader}>{dayLabel}</Text>}
              description={<Text className={styles.dayDate}>{dateLabel}</Text>}
            />
            <div className={styles.slotsColumn}>
              {daySlots.map((slot) => {
                const isSelected =
                  selectedSlot?.start === slot.start &&
                  selectedSlot?.staffUserId === slot.staffUserId;
                return (
                  <Button
                    key={`${slot.start}-${slot.staffUserId}`}
                    appearance={isSelected ? 'primary' : 'subtle'}
                    size="small"
                    className={styles.slotButton}
                    onClick={() => onSelectSlot(slot)}
                  >
                    {timeFormatter.format(new Date(slot.start))}
                    <span className={styles.staffLabel}>
                      with {slot.staffDisplayName.split(' ')[0]}
                    </span>
                  </Button>
                );
              })}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
